/**
 * RinggWidget — the assembled React Native widget.
 *
 * The native counterpart of the web assembly: the same tree, the same order,
 * the same branches. It holds NO conversation state — every snapshot comes
 * from `@ringg/core` via the shared hooks and every intent is forwarded to the
 * controller. If a behaviour looks like it belongs here, it belongs in core.
 *
 * Three things genuinely differ from web, all forced by the platform:
 *
 *  1. There is no `position: fixed`. The widget is an absolutely-filled
 *     overlay with `pointerEvents="box-none"`, so taps pass through everywhere
 *     except the trigger and the panel. Mount it as the LAST child of the app
 *     root; `widgetPosition` (a web-only config) is ignored and the panel
 *     sizes itself to the device.
 *  2. The keyboard covers the bottom of the screen. The panel lives inside a
 *     `KeyboardAvoidingView` so the composer stays visible while typing —
 *     without it the input is simply unreachable in text mode.
 *  3. The transcript cannot measure a child's offset the way web does to
 *     anchor the newest turn's first line. It pins to the bottom instead, and
 *     the "new message" / "typing toggled" distinction is preserved so a long
 *     reply arriving does not yank the view while the user is reading.
 */

import { Fragment, useEffect, useMemo, useRef, useState, type ComponentRef, type FC } from "react";
import { Animated, Image, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, View, useWindowDimensions } from "react-native";
import { Minus, X } from "lucide-react-native";
import type { Room } from "livekit-client";
import { DEFAULT_CONFIG, type ChatMessage, type MediaType, type RinggWidgetController, type SlashCommand } from "@ringg/core";
import { useRinggComponents, useRinggMessages, useRinggSession, useRinggShell, useRinggSlashCommands, useRinggTyping } from "@ringg/core/react";

import ActionButton from "./components/action-button";
import CallControls from "./components/call-controls";
import FeedbackScreen from "./components/feedback-screen";
import Header from "./components/header";
import InteractiveFlow from "./components/interactive-flow";
import LoadingBar from "./components/loading-bar";
import MessageItem from "./components/message-item";
import SystemLog from "./components/system-log";
import TypingIndicator from "./components/typing-indicator";
import { VoiceAnimationLeaf } from "./components/voice-leaves";
import BlocksStep from "./components/steps/blocks-step";
import { ConfirmDialog } from "./components/ui/dialog";
import { Touchable } from "./components/ui/pressable";
import { useMicrophone } from "./hooks/use-microphone";
import { usePresence } from "./hooks/use-presence";
import { GradientFill, solidColor } from "./lib/gradient";
import { PhoneCallRingIcon } from "./lib/icons";
import { isBlocks, isButtons, type ComponentPayload as WidgetComponentPayload, type InteractiveFlowPayload, type SimpleComponentPayload } from "./lib/component-types";
import { toSize, toViewStyle } from "./lib/portable-styles";
import { renderTextWithLinks } from "./lib/text-helpers";
import { ringgId } from "./lib/testing";
import { resolveFontFamily, resolveRadius, WidgetThemeProvider, useWidgetTheme } from "./lib/widget-theme";

export interface RinggWidgetProps {
  controller: RinggWidgetController;
  /**
   * The LiveKit room from `createLiveKitTransport()`. Optional: it only powers
   * the in-call audio visualizer and the mute button. Without it the widget is
   * fully functional and the visualizer shows its ambient idle animation.
   */
  room?: Room;
}

interface WidgetPanelProps extends RinggWidgetProps {
  width: number;
  height: number;
}

/** Web's `md:w-[400px]` / `h-[28.5rem]`, and its 96dvh clamp. */
const PANEL_MAX_WIDTH = 400;
const PANEL_HEIGHT = 456;
const PANEL_HEIGHT_RATIO = 0.9;

/** Web anchors the panel and trigger at 2% of the viewport; dp here. */
const EDGE_INSET = 16;
/**
 * Extra clearance at the bottom so the trigger never sits under the home
 * indicator. The widget deliberately takes no dependency on a safe-area
 * provider — it is mounted as a bare overlay — so this is a fixed inset that
 * clears the indicator on devices that have one and reads as ordinary spacing
 * on those that do not.
 */
const BOTTOM_SAFE_INSET = 24;
const TRIGGER_SIZE = 52;
const TRIGGER_RADIUS = 20;
const DEFAULT_TRIGGER_ICON_SIZE = 24;

/**
 * The web widget's own springs, carried over as-is. RN's `Animated.spring`
 * takes the same stiffness/damping units framer-motion uses, so these are the
 * literal values from `web/src/RinggWidget.tsx` rather than an approximation.
 */
const PANEL_SPRING = { stiffness: 380, damping: 30 };
const TRIGGER_SPRING = { stiffness: 400, damping: 25 };

const LEGAL_LINKS = { "Privacy Policy": "https://www.ringg.ai/privacy", "T&C": "https://www.ringg.ai/terms" };
const LEGAL_TEXT = "By continuing, you agree to our\nPrivacy Policy and T&C.";

const isFreeButtonsMessage = (message: ChatMessage | undefined): boolean => {
  // Core keeps `presentation` out of its ButtonsData contract — the structured
  // widget contract lives in lib/component-types, hence the cast.
  const componentData = message?.componentData as unknown as WidgetComponentPayload | undefined;
  return !!componentData && isButtons(componentData) && componentData.data.presentation === "free";
};

const isAttachableAgentMessage = (message: ChatMessage | undefined): boolean => !!message && !message.componentType && !message.isSelf && Boolean(message.message.trim());


const WidgetPanel: FC<WidgetPanelProps> = ({ controller, room, width, height }) => {
  const theme = useWidgetTheme();
  const { config } = controller;
  const {
    title = DEFAULT_CONFIG.title,
    description = DEFAULT_CONFIG.description,
    buttons,
    defaultExpanded = DEFAULT_CONFIG.defaultExpanded,
    hideTabSelector = DEFAULT_CONFIG.hideTabSelector,
    bypassStartScreen = DEFAULT_CONFIG.bypassStartScreen,
    logoUrl,
    logoStyles,
    typingWords,
    legalDisclaimer,
    feedbackScreen,
    voiceCall,
  } = config;

  // ─── Core snapshots (the ONLY state source) ────────────────────────────────
  const shell = useRinggShell(controller);
  const session = useRinggSession(controller);
  const { messages } = useRinggMessages(controller);
  const { isTyping: isAgentTyping } = useRinggTyping(controller);
  const { completedFlowIds } = useRinggComponents(controller);
  const { commands } = useRinggSlashCommands(controller);

  // The end-call confirm dialog is pure UI state — local, as on web.
  const [alertEndCall, setAlertEndCall] = useState(false);

  const microphone = useMicrophone(room);

  const callMode = shell.callMode;
  const showFeedback = shell.viewState === "feedback";

  const isLoading = session.isLoading;
  const error = session.error;
  const connectionState = session.connectionState;
  const isCalling = connectionState === "connected";
  // UI gate for the calling layout: optimistic (session created) OR actually
  // connected — computed by the core session store.
  const isSessionLive = session.isSessionLive;

  const isAudioMode = callMode === "audio";
  const showVoiceAnimation = isAudioMode && isCalling && (voiceCall?.showAnimation ?? DEFAULT_CONFIG.voiceCallShowAnimation);
  const showVoiceTranscript = !isAudioMode || (voiceCall?.showTranscript ?? DEFAULT_CONFIG.voiceCallShowTranscript);

  const mergedSlashCommands = useMemo(() => [...commands], [commands]);
  // Core hands out a readonly snapshot — copy before the (source-verbatim)
  // sort. Neighbour lookups below use the same sorted array.
  const sortedMessages = useMemo(() => [...messages].sort((a, b) => a.timestamp - b.timestamp), [messages]);

  // `ComponentRef` reads the instance type off ScrollView itself, so this
  // compiles on both RN 0.86 (PublicScrollViewInstance) and 0.87
  // (ScrollViewInstance) — the peer range spans versions that renamed it.
  const transcriptRef = useRef<ComponentRef<typeof ScrollView>>(null);
  const previousMessagesCountRef = useRef(0);

  useEffect(() => {
    const newMessageAdded = messages.length > previousMessagesCountRef.current;
    previousMessagesCountRef.current = messages.length;
    // A new turn scrolls; a typing toggle only re-pins. Animating the former
    // and not the latter keeps the dots from jittering the view every time
    // they appear and disappear.
    transcriptRef.current?.scrollToEnd({ animated: newMessageAdded });
  }, [messages, isAgentTyping]);

  // ─── Intents → controller ──────────────────────────────────────────────────
  const handleCallStart = (mediaType: MediaType): void => void controller.startCall(mediaType);
  const handleCallEnd = (): void => {
    setAlertEndCall(false);
    void controller.endCall();
  };
  const handleFeedbackSubmit = (rating: number, comment: string): void => void controller.submitFeedback(rating, comment);
  const handleFeedbackSkip = (): void => controller.skipFeedback();
  const handleUserMessageSent = (): void => controller.typing.showOnUserSend();
  const handleFlowComplete = (componentId: string): void => controller.markFlowComplete(componentId);
  const handleSelectionDisplayed = (componentId: string, label: string): void => controller.displaySelection(componentId, label);
  const sendComponentResponse = (componentId: string, responseData: Record<string, unknown>): Promise<void> => controller.sendComponentResponse(componentId, responseData);
  const sendSlashCommand = (command: SlashCommand): void => void controller.sendSlashCommand(command);
  const callComponentApi = controller.callComponentApi;

  const panelRadius = resolveRadius(theme.borderRadius, 16);
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const chrome = (
    <>
      {!defaultExpanded && (isSessionLive || showFeedback) ? (
        <Touchable testID={ringgId("minimize-button")} accessibilityLabel="Minimize widget" onPress={() => controller.minimizeWidget()} style={[styles.chromeButton, styles.minimizeButton]}>
          <Minus size={16} color={solidColor(theme.mutedTextColor)} />
        </Touchable>
      ) : null}
      {!defaultExpanded ? (
        <Touchable
          testID={ringgId("close-button")}
          accessibilityLabel={isSessionLive ? "End call" : "Close widget"}
          onPress={() => (isSessionLive ? setAlertEndCall(true) : showFeedback ? handleFeedbackSkip() : controller.minimizeWidget())}
          style={[styles.chromeButton, styles.closeButton]}
        >
          <X size={16} color={solidColor(theme.mutedTextColor)} />
        </Touchable>
      ) : null}
    </>
  );

  const transcript = (
    <ScrollView
      testID={ringgId("transcript")}
      ref={transcriptRef}
      style={styles.transcript}
      contentContainerStyle={[styles.transcriptList, !showVoiceAnimation && styles.transcriptTopClearance, !isAudioMode && styles.transcriptBottomClearance]}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      {sortedMessages.map((segment, index) => {
        // System log pill (host action fired, etc.) — before the component branches.
        if (segment.kind === "system") return <SystemLog key={`sys_${segment.timestamp}_${index}`} label={segment.message} meta={segment.systemMeta} level={segment.systemLevel} />;

        const previousSegment = sortedMessages[index - 1];
        const nextSegment = sortedMessages[index + 1];
        const attachFreeButtonsToPrevious = isFreeButtonsMessage(segment) && isAttachableAgentMessage(previousSegment);
        const skipStandaloneRender = isAttachableAgentMessage(segment) && isFreeButtonsMessage(nextSegment);

        if (skipStandaloneRender) return null;

        if (attachFreeButtonsToPrevious && previousSegment && segment.componentData) {
          // After a selected_item choice the user's pick is shown as a
          // right-aligned bubble (pushed by handleSelectionDisplayed); render
          // just the agent's prompt here, without the buttons.
          const componentData = segment.componentData as unknown as WidgetComponentPayload;
          const isCompletedSelectedItem = componentData.component_type === "buttons" && componentData.data.completionDisplay === "selected_item" && completedFlowIds.has(componentData.component_id);

          if (isCompletedSelectedItem) return <MessageItem key={`message_with_buttons_${segment.componentData.component_id}_${index}`} itemIndex={index} message={previousSegment} />;

          // The chips render as a sibling below the message (not inside its
          // width-capped bubble) so they can align to the panel edge.
          return (
            <Fragment key={`message_with_buttons_${segment.componentData.component_id}_${index}`}>
              <MessageItem itemIndex={index} message={previousSegment} />
              <View testID={ringgId("attached-buttons")} style={styles.fullWidth}>
                <InteractiveFlow
                  initialComponent={segment.componentData as unknown as SimpleComponentPayload | InteractiveFlowPayload}
                  onApiCall={callComponentApi}
                  onComplete={() => handleFlowComplete(componentData.component_id)}
                  onSendResponse={sendComponentResponse}
                  onSelectionDisplayed={handleSelectionDisplayed}
                  quickReplyAlign={isAudioMode ? "center" : "end"}
                />
              </View>
            </Fragment>
          );
        }

        // Block Kit tree — standalone, full width; keyed by component_id so a
        // re-render never wipes half-filled inputs.
        if (segment.componentType === "blocks" && segment.componentData) {
          const blocksPayload = segment.componentData as unknown as WidgetComponentPayload;
          if (isBlocks(blocksPayload)) {
            const blocksConfig = blocksPayload.data;
            const blockId = blocksPayload.component_id;
            return (
              <View key={`blocks_${blockId}`} testID={ringgId("blocks-widget")} style={styles.fullWidth}>
                <BlocksStep
                  config={blocksConfig}
                  // One-shot: a respond is a conversation turn, so the card
                  // locks once answered.
                  disabled={completedFlowIds.has(blockId)}
                  onAction={(action) => {
                    handleUserMessageSent();
                    handleFlowComplete(blockId);
                    // Echo the selection as a right-aligned user bubble, like a
                    // quick-reply pick. Prefer the button's `value` — it is the
                    // meaningful choice (identical labels like "View details"
                    // share a value that differs, e.g. the plan name) — and fall
                    // back to the label only when there is no usable value.
                    const selection = typeof action.value === "string" || typeof action.value === "number" ? String(action.value) : action.label;
                    if (selection) handleSelectionDisplayed(blockId, selection);
                    // `label` stays UI-only — the wire action carries
                    // action_id/value/values, exactly like the source.
                    return controller.sendBlocksAction(blocksConfig.tool_id, blockId, { action_id: action.action_id, value: action.value, values: action.values });
                  }}
                />
              </View>
            );
          }
        }

        if (segment.componentType && segment.componentData) {
          // Same selected_item case for a standalone (non-attached) button flow
          // — the pick is the right-aligned bubble below, so unmount the flow.
          const componentData = segment.componentData as unknown as WidgetComponentPayload;
          const isCompletedSelectedItem = componentData.component_type === "buttons" && componentData.data.completionDisplay === "selected_item" && completedFlowIds.has(componentData.component_id);
          if (isCompletedSelectedItem) return null;

          return (
            <InteractiveFlow
              key={`flow_${segment.componentData.component_id}_${index}`}
              initialComponent={segment.componentData as unknown as SimpleComponentPayload | InteractiveFlowPayload}
              onApiCall={callComponentApi}
              onComplete={() => handleFlowComplete(componentData.component_id)}
              onSendResponse={sendComponentResponse}
              onSelectionDisplayed={handleSelectionDisplayed}
              quickReplyAlign={isAudioMode ? "center" : "end"}
            />
          );
        }

        return <MessageItem key={`${segment.timestamp}__${index}`} itemIndex={index} message={segment} />;
      })}
      {isAgentTyping ? <TypingIndicator words={typingWords} /> : null}
    </ScrollView>
  );

  const controls = (
    <CallControls
      buttonConfig={buttons}
      callMode={callMode}
      isCalling={isSessionLive}
      enabledSlashCommands={mergedSlashCommands}
      micEnabled={microphone?.enabled ?? true}
      onToggleMic={microphone?.toggle}
      onSlashCommand={sendSlashCommand}
      onUserMessage={handleUserMessageSent}
      onCallEnd={handleCallEnd}
      onSendMessage={(message) => controller.sendMessage(message)}
    />
  );

  return (
    <View
      testID={ringgId("widget-root")}
      style={[styles.panel, { width, height, borderRadius: panelRadius, backgroundColor: solidColor(theme.backgroundColor), borderColor: solidColor(theme.borderColor) }]}
    >
      {chrome}

      <ConfirmDialog
        open={alertEndCall}
        title={`Are you sure you want to end ${callMode === "text" ? "chat" : "call"}?`}
        description="Your current conversation will be closed and cannot be accessed later."
        confirmLabel={`End ${callMode === "text" ? "Chat" : "Call"}`}
        cancelLabel="Cancel"
        onConfirm={handleCallEnd}
        onCancel={() => setAlertEndCall(false)}
      />

      {showFeedback ? (
        <FeedbackScreen
          callMode={callMode}
          isSubmitting={isLoading}
          feedbackScreenConfig={feedbackScreen}
          messages={messages}
          title={title}
          logoUrl={logoUrl}
          logoStyles={logoStyles}
          onSubmit={handleFeedbackSubmit}
          onSkip={handleFeedbackSkip}
        />
      ) : (
        <View testID={ringgId("widget-body")} style={[styles.body, isSessionLive ? styles.bodyLive : styles.bodyIdle]}>
          <Header isCalling={isSessionLive} isLoading={isLoading} title={title} description={description} logoUrl={logoUrl} logoStyles={logoStyles} />

          {showVoiceAnimation ? (
            <View testID={ringgId("voice-animation-wrapper")} style={[styles.voiceWrapper, showVoiceTranscript && messages.length > 0 ? styles.voiceWrapperCompact : styles.voiceWrapperFull]}>
              <VoiceAnimationLeaf room={room} />
            </View>
          ) : null}

          {isSessionLive && !showVoiceAnimation && ((messages.length === 0 && !isAgentTyping) || !showVoiceTranscript) ? <View testID={ringgId("body-spacer")} style={styles.bodySpacer} /> : null}

          {(messages.length > 0 || isAgentTyping) && isSessionLive && showVoiceTranscript ? transcript : null}

          {isSessionLive && callMode === "text" ? (
            // Transparent bottom bar: it only positions the composer over the
            // transcript, so the gutters stay clear while messages scroll under.
            <View testID={ringgId("controls-bar")} style={styles.controlsBar}>
              {error.hasError ? (
                <Text testID={ringgId("error-message")} style={[styles.error, { color: solidColor(theme.errorColor), fontFamily }]}>
                  {error.message}
                </Text>
              ) : null}
              {controls}
            </View>
          ) : (
            <>
              {controls}
              {error.hasError ? (
                <Text testID={ringgId("error-message")} style={[styles.error, { color: solidColor(theme.errorColor), fontFamily }]}>
                  {error.message}
                </Text>
              ) : null}
            </>
          )}

          {!isSessionLive && isLoading ? (
            <View testID={ringgId("connecting-status")}>
              <Text testID={ringgId("connecting-label")} style={[styles.connecting, { color: theme.mutedTextColor, fontFamily }]}>
                Connecting...
              </Text>
              <LoadingBar ariaLabel={callMode === "text" ? "Starting chat" : "Connecting"} />
            </View>
          ) : null}

          {!isLoading && connectionState === "disconnected" && (!bypassStartScreen || error.hasError) ? (
            hideTabSelector ? (
              <ActionButton callMode={callMode} buttonConfig={buttons} isCalling={isCalling} isLoading={isLoading} onCallTrigger={handleCallStart} />
            ) : (
              <View testID={ringgId("start-actions")} style={styles.startActions}>
                <ActionButton dual callMode="text" buttonConfig={buttons} isCalling={isCalling} isLoading={isLoading} onCallTrigger={handleCallStart} />
                <ActionButton dual callMode="audio" buttonConfig={buttons} isCalling={isCalling} isLoading={isLoading} onCallTrigger={handleCallStart} />
              </View>
            )
          ) : null}

          {!isSessionLive ? (
            <Text testID={ringgId("legal-disclaimer")} style={[styles.legal, { color: theme.mutedTextColor, fontFamily }]}>
              {legalDisclaimer ? renderTextWithLinks(legalDisclaimer.text, legalDisclaimer.links) : renderTextWithLinks(LEGAL_TEXT, LEGAL_LINKS, { color: solidColor(theme.primaryColor), textDecorationLine: "underline" })}
            </Text>
          ) : null}
        </View>
      )}
    </View>
  );
};

export const RinggWidget: FC<RinggWidgetProps> = ({ controller, room }) => {
  const shell = useRinggShell(controller);
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const { buttons, defaultExpanded = DEFAULT_CONFIG.defaultExpanded, widgetPosition } = controller.config;

  const isOpen = shell.viewState === "open" || shell.viewState === "feedback";
  const hideTriggerOnExpand = widgetPosition?.hideTriggerOnExpand ?? true;
  const showTrigger = !defaultExpanded && (!hideTriggerOnExpand || !isOpen);

  // `mounted` outlives `isOpen` for the length of the exit spring — without
  // that, the panel would unmount in the same commit and its close animation
  // would drive a value nothing is attached to.
  const panel = usePresence(isOpen, PANEL_SPRING);
  const trigger = usePresence(showTrigger, TRIGGER_SPRING);
  // Derived from the inset, not from a fraction of the viewport. Web sizes the
  // panel at 95vw and offsets it 2% from the right, which overlaps by design
  // there; here the anchor asks for a real margin on BOTH sides, so a width
  // that does not leave room for two of them overflows its own margin box and
  // the panel drifts left. Phone: fills the width between equal insets.
  // Tablet: caps at 400 and stays anchored right, as on web.
  const panelWidth = Math.min(screenWidth - EDGE_INSET * 2, PANEL_MAX_WIDTH);
  const panelHeight = Math.min(screenHeight * PANEL_HEIGHT_RATIO, PANEL_HEIGHT);

  const triggerIconSize = toSize(buttons?.modalTrigger?.icon?.size, DEFAULT_TRIGGER_ICON_SIZE);

  return (
    <WidgetThemeProvider theme={controller.theme}>
      {/* box-none: the overlay itself is transparent to touches, its children
          are not — so the host app underneath stays fully interactive. */}
      <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
        {panel.mounted ? (
          <KeyboardAvoidingView pointerEvents="box-none" behavior={Platform.OS === "ios" ? "padding" : undefined} style={styles.overlay}>
            <Animated.View
              // Android redraws an elevation shadow from the view outline on
              // every frame of a transform. Rasterising the layer hands that to
              // the GPU instead, which is what makes the open and close smooth
              // rather than steppy.
              renderToHardwareTextureAndroid
              shouldRasterizeIOS
              style={[
                styles.panelAnchor,
                {
                  opacity: panel.progress,
                  transform: [{ translateY: panel.progress.interpolate({ inputRange: [0, 1], outputRange: [24, 0] }) }, { scale: panel.progress.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] }) }],
                },
              ]}
            >
              <WidgetPanel controller={controller} room={room} width={panelWidth} height={panelHeight} />
            </Animated.View>
          </KeyboardAvoidingView>
        ) : null}

        {trigger.mounted ? <WidgetTrigger controller={controller} iconSize={triggerIconSize} progress={trigger.progress} /> : null}
      </View>
    </WidgetThemeProvider>
  );
};

/**
 * Split out so it can read the theme context the provider above installs — a
 * component cannot consume a context its own parent creates.
 */
const WidgetTrigger: FC<{ controller: RinggWidgetController; iconSize: number; progress: Animated.Value }> = ({ controller, iconSize, progress }) => {
  const theme = useWidgetTheme();
  const { buttons } = controller.config;

  return (
    // Web pops the trigger with a spring and fades it out again when the panel
    // takes over; both ends of that are visible on every open/close cycle.
    <Animated.View style={[styles.trigger, { opacity: progress, transform: [{ scale: progress.interpolate({ inputRange: [0, 1], outputRange: [0.8, 1] }) }] }]}>
      <Touchable testID={ringgId("trigger-button")} accessibilityLabel="Open call widget" onPress={() => controller.handleTriggerClick()}>
      <GradientFill color={theme.primaryColor} style={[styles.triggerSurface, toViewStyle(buttons?.modalTrigger?.styles)]}>
        {/* Web's default trigger icon is a remote SVG. RN's <Image> decodes
            PNG/JPEG/GIF/WebP but NOT SVG, so that URL renders an empty button
            here — the same glyph ships in this package as a vector instead.
            An integrator-supplied `icon.url` still goes through <Image>, which
            is correct for the raster formats they can actually use. */}
        {buttons?.modalTrigger?.icon?.url ? (
          <Image
            testID={ringgId("trigger-icon")}
            accessibilityLabel="Widget trigger"
            source={{ uri: buttons.modalTrigger.icon.url }}
            resizeMode="contain"
            style={{ width: iconSize, height: iconSize }}
          />
        ) : (
          <PhoneCallRingIcon testID={ringgId("trigger-icon")} size={iconSize} fill={solidColor(theme.primaryTextColor)} />
        )}
        </GradientFill>
      </Touchable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  overlay: { ...StyleSheet.absoluteFill, justifyContent: "flex-end", alignItems: "flex-end" },
  panelAnchor: { marginHorizontal: EDGE_INSET, marginTop: EDGE_INSET, marginBottom: BOTTOM_SAFE_INSET },
  panel: {
    borderWidth: StyleSheet.hairlineWidth,
    // `overflow: hidden` is what keeps the transcript, the header bar and the
    // rounded corners agreeing with each other.
    overflow: "hidden",
    // Web stacks three shadows at 1-2% alpha — the panel sits just off the page
    // rather than floating above it. 12% read as a hard slab, and on Android
    // `elevation` is the only one of these that applies, so it was the entire
    // effect instead of a supporting layer.
    shadowColor: "#000000",
    shadowOpacity: 0.05,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },

  trigger: { position: "absolute", right: EDGE_INSET, bottom: BOTTOM_SAFE_INSET },
  triggerSurface: { width: TRIGGER_SIZE, height: TRIGGER_SIZE, borderRadius: TRIGGER_RADIUS, alignItems: "center", justifyContent: "center" },

  chromeButton: { position: "absolute", top: 10, zIndex: 20, width: 28, height: 28, borderRadius: 14, alignItems: "center", justifyContent: "center" },
  minimizeButton: { right: 42 },
  closeButton: { right: 10 },

  body: { flex: 1 },
  bodyLive: { paddingHorizontal: 20, paddingVertical: 8, gap: 10 },
  bodyIdle: { padding: 24, gap: 12 },
  bodySpacer: { flex: 1, marginTop: 56 },

  voiceWrapper: { width: "100%", alignItems: "stretch", justifyContent: "center", marginTop: 56 },
  voiceWrapperCompact: { height: 56, flexShrink: 0 },
  voiceWrapperFull: { flex: 1, minHeight: 0 },

  transcript: { flex: 1 },
  // `justifyContent: flex-end` is web's `mt-auto`: a short conversation sits at
  // the bottom of the panel rather than floating at the top.
  transcriptList: { flexGrow: 1, justifyContent: "flex-end", gap: 12 },
  transcriptTopClearance: { paddingTop: 64 },
  transcriptBottomClearance: { paddingBottom: 80 },
  fullWidth: { width: "100%" },

  controlsBar: { position: "absolute", zIndex: 10, bottom: 0, left: 0, right: 0, paddingHorizontal: 20, paddingVertical: 12 },
  error: { textAlign: "center", fontSize: 14, marginBottom: 8 },
  connecting: { fontSize: 14, textAlign: "center" },
  startActions: { flexDirection: "row", alignItems: "center", gap: 8, width: "100%" },
  legal: { fontSize: 12, lineHeight: 20, textAlign: "center" },
});
